import React, { useState, useEffect } from 'react';
import { Link, useOutletContext, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db, auth } from './firebaseConfig';
import { collection, getDocs, doc, query, where, setDoc, deleteDoc, updateDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import Select from 'react-select';
import QRCode from 'react-qr-code';
import TalentSearch from './TalentSearch';
import TalentCardModal from './TalentCardModal';
import { Home, PlusCircle, Users, Building, Mail, Bot, QrCode, Edit2, Trash2, Check, X, BarChart2, RefreshCw, Printer, ArrowLeft, FileText } from 'lucide-react';
import './LinkedInTheme.css';

function AdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userProfile } = useOutletContext();
  const isUserAdmin = userProfile?.roles?.includes('admin');
  const [activeView, setActiveView] = useState('talentCardSearch');

  // State for creating orgs
  const [orgId, setOrgId] = useState('');
  const [orgName, setOrgName] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);

  // State for assigning users
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState({ hr: false, hm: false, sales: false, admin: false });
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [assignSuccess, setAssignSuccess] = useState(null);

  // State for Talent Card Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('name'); // name, skill, email
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  // State for Marketing Campaigns
  const [campaigns, setCampaigns] = useState([]);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [newCampaignHook, setNewCampaignHook] = useState(''); 
  const [newCampaignLocation, setNewCampaignLocation] = useState('');
  const [newCampaignNotes, setNewCampaignNotes] = useState('');
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false); 
  const [selectedCampaign, setSelectedCampaign] = useState(null); 
  const [campaignStats, setCampaignStats] = useState({ users: [], loading: false });
  
  // Inline Edit/Delete State
  const [editingId, setEditingId] = useState(null);
  const [tempName, setTempName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // State for Platform Stats
  const [statsData, setStatsData] = useState({ users: 0, simulations: 0, campaigns: 0 });
  const [totalStats, setTotalStats] = useState({ users: 0, orgs: 0, vtcs: 0 }); 
  const [statsLoading, setStatsLoading] = useState(false);
  const [timeRange, setTimeRange] = useState('today'); // 'today', 'week', 'month', 'all'

  // Label Printer State
  const [labelConfig, setLabelConfig] = useState({
      rows: 4,
      cols: 2,
      shape: 'rect',
      showHook: true,
      fontSize: 12,
      qrSize: 80,
      padding: 5,
      // Manual Controls (mm)
      cellWidth: 90,
      cellHeight: 60,
      marginTop: 10,
      marginLeft: 10,
      marginRight: 10,
      gap: 2
  });

  const fetchInitialData = async () => {
    try {
      // Fetch Orgs
      const orgsSnapshot = await getDocs(collection(db, 'organizations'));
      const orgs = orgsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrganizations(orgs);

      // Fetch Campaigns (Marketing)
      const user = auth.currentUser;
      if (user) {
          try {
            const campaignsRef = collection(db, 'marketingCampaigns');
            const q = query(campaignsRef, orderBy('createdAt', 'desc'));
            const campSnapshot = await getDocs(q);
            const camps = campSnapshot.docs.map(doc => ({ code: doc.id, ...doc.data() }));
            setCampaigns(camps);
          } catch (e) {
              console.warn("Could not fetch campaigns (permissions might not be ready yet):", e);
          }
      }

      // Fetch Users
      if (!user) throw new Error('You must be logged in.');
      const idToken = await user.getIdToken();
      const usersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/getUsers`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (!usersResponse.ok) throw new Error('Failed to fetch users.');
      const usersData = await usersResponse.json();
      setUsers(usersData.users);

    } catch (err) {
      console.error("Error fetching initial data:", err);
      setCreateError(err.message); 
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // When a user is selected, pre-fill the form with their data
  useEffect(() => {
    if (selectedUser) {
      const userRoles = selectedUser.profile?.roles || [];
      setSelectedRoles({
        hr: userRoles.includes('hr'),
        hm: userRoles.includes('hm'),
        sales: userRoles.includes('sales'),
        admin: userRoles.includes('admin'),
      });
      const orgOptions = organizations.map(org => ({ value: org.id, label: `${org.name} (${org.id})` }));
      const userOrg = orgOptions.find(o => o.value === selectedUser.profile?.orgId);
      setSelectedOrg(userOrg || null);
    } else {
      // Clear form when user is deselected
      setSelectedRoles({ hr: false, hm: false, sales: false, admin: false });
      setSelectedOrg(null);
    }
  }, [selectedUser, organizations]);

  const generateHexCode = () => {
      // Generate a random 5-char hex string
      return Math.floor(Math.random() * 0xFFFFF).toString(16).toUpperCase().padStart(5, '0');
  };

  const handleCreateCampaign = async (e) => {
      e.preventDefault();
      if (!newCampaignName.trim()) return;
      setCampaignLoading(true);
      try {
          const code = generateHexCode();
          const campaignData = {
              name: newCampaignName,
              hook: newCampaignHook || '',
              location: newCampaignLocation || '',
              notes: newCampaignNotes ? [newCampaignNotes] : [],
              url: `${window.location.origin}/login?ref=${code}`,
              createdAt: serverTimestamp(),
              createdBy: auth.currentUser.uid
          };
          // Use 'code' as the document ID for easy lookup
          await setDoc(doc(db, 'marketingCampaigns', code), campaignData);
          setCampaigns([{ code, ...campaignData }, ...campaigns]); // Add to top since we sort by desc
          setNewCampaignName('');
          setNewCampaignHook('');
          setNewCampaignLocation('');
          setNewCampaignNotes('');
          setShowCreateForm(false);
      } catch (err) {
          console.error("Error creating campaign:", err);
          alert("Failed to create campaign: " + err.message);
      } finally {
          setCampaignLoading(false);
      }
  };

  const startEditing = (camp) => {
      setEditingId(camp.code);
      setTempName(camp.name);
      setDeleteConfirmId(null); // Close any open delete confirms
  };

  const saveEditing = async (camp) => {
      if (!tempName.trim() || tempName === camp.name) {
          setEditingId(null);
          return;
      }
      try {
          await updateDoc(doc(db, 'marketingCampaigns', camp.code), {
              name: tempName.trim()
          });
          setCampaigns(campaigns.map(c => 
              c.code === camp.code ? { ...c, name: tempName.trim() } : c
          ));
          setEditingId(null);
      } catch (err) {
          console.error("Error updating campaign:", err);
          alert("Failed to update name: " + err.message);
      }
  };

  const handleDeleteCampaign = async (campaignCode) => {
      // If not yet confirmed, show confirmation UI
      if (deleteConfirmId !== campaignCode) {
          setDeleteConfirmId(campaignCode);
          setEditingId(null); // Close any open edits
          return;
      }

      // Proceed with delete
      try {
          await deleteDoc(doc(db, 'marketingCampaigns', campaignCode));
          setCampaigns(campaigns.filter(c => c.code !== campaignCode));
          setDeleteConfirmId(null);
      } catch (err) {
          console.error("Error deleting campaign:", err);
          alert("Failed to delete campaign: " + err.message);
      }
  };

  const fetchCampaignStats = async (campaign) => {
      setSelectedCampaign(campaign);
      setCampaignStats({ users: [], loading: true });
      setActiveView('campaignDetails');
      
      try {
          // Query users where profile.referralSource matches the campaign code
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where("profile.referralSource", "==", campaign.code));
          const querySnapshot = await getDocs(q);
          
          const users = querySnapshot.docs.map(doc => ({
              uid: doc.id,
              ...doc.data()
          }));
          
          setCampaignStats({ users, loading: false });
      } catch (err) {
          console.error("Error fetching stats:", err);
          setCampaignStats({ users: [], loading: false, error: err.message });
      }
  };

  const fetchTotalStats = async () => {
      try {
          const usersRef = collection(db, 'users');
          const orgsRef = collection(db, 'organizations');
          const vtcsRef = collection(db, 'talentCards');

          const [totalUsers, totalOrgs, totalVtcs] = await Promise.all([
              getDocs(usersRef),
              getDocs(orgsRef),
              getDocs(vtcsRef)
          ]);
          
          setTotalStats({
              users: totalUsers.size,
              orgs: totalOrgs.size,
              vtcs: totalVtcs.size
          });
      } catch (err) {
          console.error("Error fetching total stats:", err);
      }
  };

  const fetchGrowthStats = async (range) => {
      setStatsLoading(true);
      setTimeRange(range);
      
      try {
          const now = new Date();
          let startDate = new Date();
          
          if (range === 'today') {
              startDate.setHours(0, 0, 0, 0);
          } else if (range === 'week') {
              const day = startDate.getDay() || 7; // Get current day number, converting Sun. to 7
              if (day !== 1) startDate.setHours(-24 * (day - 1));
              startDate.setHours(0, 0, 0, 0);
          } else if (range === 'month') {
              startDate.setDate(1);
              startDate.setHours(0, 0, 0, 0);
          } else {
              startDate = new Date(0); // All time
          }

          // Queries
          const usersRef = collection(db, 'users');
          const simsRef = collection(db, 'simulations');
          const campsRef = collection(db, 'marketingCampaigns');

          const [usersSnap, simsSnap, campsSnap] = await Promise.all([
              getDocs(query(usersRef, where("createdAt", ">=", startDate))),
              getDocs(query(simsRef, where("createdAt", ">=", startDate))),
              getDocs(query(campsRef, where("createdAt", ">=", startDate)))
          ]);

          setStatsData({
              users: usersSnap.size,
              simulations: simsSnap.size,
              campaigns: campsSnap.size
          });

      } catch (err) {
          console.error("Error fetching platform stats:", err);
      } finally {
          setStatsLoading(false);
      }
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    if (!orgId || !orgName) {
      setCreateError("Organization ID and Name are required.");
      return;
    }
    setCreateLoading(true);
    setCreateError(null);
    setCreateSuccess(null);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in.');
      const idToken = await user.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/createOrganization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ orgId, orgName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create organization.');
      }

      setCreateSuccess(`Organization "${orgName}" created successfully!`);
      setOrgId('');
      setOrgName('');
      fetchInitialData(); // Refresh the list
    } catch (err) {
      setCreateError(err.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAssignUser = async (e) => {
    e.preventDefault();
    if (!selectedUser || !selectedOrg) {
      setAssignError("Please select a user and an organization.");
      return;
    }
    
    setAssignLoading(true);
    setAssignError(null);
    setAssignSuccess(null);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in.');
      const idToken = await user.getIdToken();

      const finalRoles = new Set(selectedUser.profile?.roles || ['candidate']);
      Object.keys(selectedRoles).forEach(role => {
        if (selectedRoles[role]) {
          finalRoles.add(role);
        } else {
          finalRoles.delete(role);
        }
      });

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateUserConfiguration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ 
          targetUserId: selectedUser.value, 
          orgId: selectedOrg.value,
          roles: Array.from(finalRoles)
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update user configuration.');
      }
      
      setAssignSuccess('User configuration updated successfully!');
      fetchInitialData(); // Refresh user list
      setSelectedUser(null);

    } catch (err) {
      setAssignError(err.message);
    } finally {
      setAssignLoading(false);
    }
  };

  const handleTalentCardSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchError("Please enter a search term.");
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    setSearchResults([]);
    setHasSearched(true);

    try {
      let finalResults = [];
      if (searchType === 'email') {
        const user = auth.currentUser;
        if (!user) throw new Error('Authentication token not found.');
        const idToken = await user.getIdToken();
        const userSearchResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/findUserByEmail`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
            },
            body: JSON.stringify({ email: searchQuery.trim() }),
        });

        if (!userSearchResponse.ok) {
            if (userSearchResponse.status === 404) {
              setSearchResults([]);
              setSearchLoading(false);
              return;
            }
            throw new Error('Error finding user by email.');
        }

        const userData = await userSearchResponse.json();
        const userId = userData.uid;

        const cardsRef = collection(db, 'talentCards');
        const cardsQuery = query(cardsRef, where("userId", "==", userId));
        const cardsSnapshot = await getDocs(cardsQuery);
        finalResults = cardsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      } else {
        const cardsRef = collection(db, 'talentCards');
        const q = searchType === 'name' 
          ? query(cardsRef, where("name", "==", searchQuery.trim()))
          : query(cardsRef, where("skills", "array-contains", searchQuery.trim()));
        
        const querySnapshot = await getDocs(q);
        finalResults = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      }
      setSearchResults(finalResults);
    } catch (err) {
      console.error("Error searching talent cards:", err);
      setSearchError("An error occurred while searching. Please check the console.");
    } finally {
      setSearchLoading(false);
    }
  };
  
  const handleDeleteOrganization = async (orgIdToDelete) => {
    if (!window.confirm(t('adminDashboard.deleteConfirm', { orgId: orgIdToDelete }))) {
      return;
    }
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in.');
      const idToken = await user.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/deleteOrganization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ orgId: orgIdToDelete }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete organization.');
      }
      
      fetchInitialData(); // Refresh the list
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const userOptions = users.map(user => ({
    value: user.id,
    label: user.profile?.email || user.id,
    ...user
  }));
  const orgOptions = organizations.map(org => ({ value: org.id, label: `${org.name} (${org.id})` }));

  const formatOptionLabel = ({ profile }) => (
    <div className="user-select-option">
      <div className="user-select-option__email">{profile?.email}</div>
      <div className="user-select-option__details">
        Name: {profile?.firstName || 'N/A'} {profile?.lastName || ''} | 
        Org: {profile?.orgId || 'N/A'} | 
        Roles: {profile?.roles?.join(', ') || 'candidate'}
      </div>
    </div>
  );

  const renderActiveView = () => {
    switch (activeView) {
      case 'talentCardSearch':
        return (
          <div className="li-card li-create-post-card">
            <div className="li-card-header">
                <h3>{t('adminDashboard.talentCardSearchTitle')}</h3>
            </div>
            <div className="li-card-body">
                <form onSubmit={handleTalentCardSearch} className="li-form-stacked">
                <div className="li-form-row">
                    <input 
                    type="text" 
                    className="li-input"
                    value={searchQuery} 
                    onChange={(e) => setSearchQuery(e.target.value)} 
                    placeholder={t('adminDashboard.searchPlaceholder')} 
                    />
                    <select className="li-input" value={searchType} onChange={(e) => setSearchType(e.target.value)}>
                        <option value="name">{t('adminDashboard.searchByName')}</option>
                        <option value="skill">{t('adminDashboard.searchBySkill')}</option>
                        <option value="email">{t('adminDashboard.searchByEmail')}</option>
                    </select>
                </div>
                <button type="submit" className="li-btn-primary" disabled={searchLoading}>
                    {searchLoading ? t('adminDashboard.searchingButton') : t('adminDashboard.searchButton')}
                </button>
                {searchError && <div className="li-error-msg">{searchError}</div>}
                </form>
                <div className="search-results">
                {hasSearched && !searchLoading && searchResults.length === 0 && (
                    <p style={{padding: '16px', textAlign: 'center'}}>{t('adminDashboard.noResults')}</p>
                )}
                {searchResults.map(card => (
                    <div key={card.id} className="li-feed-post" style={{borderTop: '1px solid #e0e0e0', marginTop: '16px'}}>
                        <div className="li-post-header">
                            <div className="li-post-actor-image" style={{width:'40px', height:'40px', borderRadius:'50%', backgroundColor:'#f3f2ef'}}></div>
                            <div className="li-post-meta">
                                <span className="li-post-title">{card.name}</span>
                                <span className="li-post-subtitle">{card.title}</span>
                            </div>
                        </div>
                        <div className="li-post-content">
                            <div className="skills-list" style={{display:'flex', gap:'4px', flexWrap:'wrap'}}>
                                {card.skills?.slice(0, 5).map((skill, i) => <span key={i} className="skill-tag" style={{background:'#f3f2ef', padding:'2px 8px', borderRadius:'4px', fontSize:'12px'}}>{skill}</span>)}
                            </div>
                        </div>
                        <div className="li-post-footer-actions">
                            <button onClick={() => { setSelectedCard(card); setIsModalOpen(true); }} className="li-action-btn">
                                {t('adminDashboard.viewCard')}
                            </button>
                        </div>
                    </div>
                ))}
                </div>
            </div>
          </div>
        );
      case 'createOrg':
        return (
          <div className="li-card li-create-post-card">
             <div className="li-create-body">
                <h3>{t('adminDashboard.createOrgTitle')}</h3>
                <form onSubmit={handleCreateOrganization}>
                  <div className="li-form-row">
                    <input
                      type="text"
                      className="li-input"
                      value={orgId}
                      onChange={(e) => setOrgId(e.target.value)}
                      placeholder={t('adminDashboard.orgIdPlaceholder')}
                    />
                    <input
                      type="text"
                      className="li-input"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      placeholder={t('adminDashboard.orgNamePlaceholder')}
                    />
                  </div>
                  <div className="li-create-actions">
                    <button type="submit" className="li-btn-primary" disabled={createLoading}>
                      {createLoading ? t('adminDashboard.creatingButton') : t('adminDashboard.createButton')}
                    </button>
                  </div>
                  {createError && <div className="li-error-msg">{createError}</div>}
                  {createSuccess && <div className="li-success-msg">{createSuccess}</div>}
                </form>
             </div>
          </div>
        );
      case 'assignUser':
        return (
          <div className="li-card li-create-post-card">
             <div className="li-create-body">
                <h3>{t('adminDashboard.assignConfigTitle')}</h3>
                <form onSubmit={handleAssignUser}>
                   <div className="li-form-stacked">
                      <label>{t('adminDashboard.userLabel')}</label>
                      <Select
                        options={userOptions}
                        value={selectedUser}
                        onChange={setSelectedUser}
                        formatOptionLabel={formatOptionLabel}
                        placeholder={t('adminDashboard.userPlaceholder')}
                        isClearable
                        classNamePrefix="li-select"
                      />
                   </div>
                   <div className="li-form-stacked">
                      <label>{t('adminDashboard.orgLabel')}</label>
                      <Select
                        options={orgOptions}
                        value={selectedOrg}
                        onChange={setSelectedOrg}
                        placeholder={t('adminDashboard.orgPlaceholder')}
                        isClearable
                        classNamePrefix="li-select"
                      />
                   </div>
                   
                   <div className="li-roles-group">
                      <span className="li-roles-label">{t('adminDashboard.rolesLabel')}</span>
                      {Object.keys(selectedRoles).map(role => (
                        <label key={role} className="li-checkbox-row">
                            <input 
                                type="checkbox" 
                                checked={selectedRoles[role]} 
                                onChange={(e) => setSelectedRoles({...selectedRoles, [role]: e.target.checked})} 
                                disabled={role === 'admin' && !isUserAdmin} 
                            />
                            <span>{role.toUpperCase()}</span>
                        </label>
                      ))}
                   </div>

                   <button type="submit" className="li-btn-primary" disabled={assignLoading}>
                      {assignLoading ? t('adminDashboard.updatingButton') : t('adminDashboard.updateButton')}
                   </button>

                   {assignError && <div className="li-error-msg">{assignError}</div>}
                   {assignSuccess && <div className="li-success-msg">{assignSuccess}</div>}
                </form>
             </div>
          </div>
        );
      case 'orgList':
        return (
          <div className="li-feed-items">
            <h3>{t('adminDashboard.existingOrgsTitle')}</h3>
            {organizations.map(org => (
              <div key={org.id} className="li-card li-feed-post">
                   <div className="li-post-header">
                      <div className="li-post-actor-image">
                         <Building size={24} />
                      </div>
                      <div className="li-post-meta">
                         <span className="li-post-title">{org.name}</span>
                         <span className="li-post-subtitle">ID: {org.id}</span>
                      </div>
                      <button onClick={() => handleDeleteOrganization(org.id)} className="li-icon-btn-ghost" style={{color: '#ef4444'}}>
                          <svg fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="20" height="20"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                      </button>
                   </div>
                   <div className="li-post-footer-actions">
                      <Link to={`/talent-pool/${org.id}`} className="li-action-btn">
                         {t('sharedTalentPool.title')}
                      </Link>
                      <Link to={`/organization-settings/${org.id}`} className="li-action-btn">
                         {t('edit', 'Edit')}
                      </Link>
                   </div>
              </div>
            ))}
          </div>
        );
      case 'marketing':
        return (
            <div className="li-card li-create-post-card">
                <div className="li-card-header" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <h3>Marketing Campaigns (QR Codes)</h3>
                    <button 
                        onClick={() => setShowCreateForm(!showCreateForm)} 
                        className="li-btn-outline-primary"
                        style={{fontSize: '14px', padding: '6px 12px'}}
                    >
                        {showCreateForm ? 'Cancel' : '+ New Campaign'}
                    </button>
                </div>
                <div className="li-card-body">
                    {showCreateForm && (
                        <div style={{background: '#f8f8f8', padding: '16px', borderRadius: '8px', marginBottom: '24px', border: '1px solid #e0e0e0'}}>
                            <h4 style={{marginTop: 0, marginBottom: '12px', fontSize: '16px'}}>Create New Tracking Code</h4>
                            <form onSubmit={handleCreateCampaign} style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                                <input 
                                    type="text" 
                                    className="li-input" 
                                    placeholder="Campaign Name (e.g. Hatfield Library)" 
                                    value={newCampaignName}
                                    onChange={(e) => setNewCampaignName(e.target.value)}
                                />
                                <input 
                                    type="text" 
                                    className="li-input" 
                                    placeholder="Hook / CTA Message (e.g. 'Scan to Find Work')" 
                                    value={newCampaignHook}
                                    onChange={(e) => setNewCampaignHook(e.target.value)}
                                />
                                <input 
                                    type="text" 
                                    className="li-input" 
                                    placeholder="Location (e.g. 'Hatfield Library')" 
                                    value={newCampaignLocation}
                                    onChange={(e) => setNewCampaignLocation(e.target.value)}
                                />
                                <textarea 
                                    className="li-input" 
                                    placeholder="Strategy Notes / Psychology (e.g. 'Targeting tech students')" 
                                    value={newCampaignNotes}
                                    onChange={(e) => setNewCampaignNotes(e.target.value)}
                                    rows={2}
                                    style={{resize: 'vertical'}}
                                />
                                <div style={{display: 'flex', gap: '8px'}}>
                                    <button type="submit" className="li-btn-primary" disabled={campaignLoading}>
                                        {campaignLoading ? 'Creating...' : 'Create Campaign'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    <div className="li-feed-items">
                        {campaigns.map(camp => (
                            <div key={camp.code} className="li-card li-feed-post" style={{marginBottom: '16px', border: '1px solid #e0e0e0', padding: 0}}>
                                <div style={{padding: '16px', display: 'flex', alignItems: 'flex-start', borderBottom: '1px solid #f3f2ef'}}>
                                    <div style={{background: '#fff', padding: '8px', border: '1px solid #eee', borderRadius: '8px'}}>
                                        <QRCode value={camp.url} size={80} />
                                    </div>
                                    <div className="li-post-meta" style={{marginLeft: '16px', flex: 1}}>
                                        {editingId === camp.code ? (
                                            <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                                                <input 
                                                    type="text" 
                                                    value={tempName} 
                                                    onChange={(e) => setTempName(e.target.value)}
                                                    className="li-input"
                                                    style={{padding: '4px 8px', fontSize: '14px', width: '100%'}}
                                                    autoFocus
                                                />
                                                <button onClick={() => saveEditing(camp)} style={{background:'none', border:'none', color:'#22c55e', cursor:'pointer'}}><Check size={18}/></button>
                                                <button onClick={() => setEditingId(null)} style={{background:'none', border:'none', color:'#666', cursor:'pointer'}}><X size={18}/></button>
                                            </div>
                                        ) : (
                                            <span className="li-post-title">{camp.name}</span>
                                        )}
                                        <div style={{marginTop: '4px'}}>
                                            <span className="li-post-subtitle" style={{fontFamily: 'monospace', fontSize: '14px', color: '#0a66c2'}}>Code: {camp.code}</span>
                                        </div>
                                        {camp.hook && <div style={{fontSize: '13px', fontWeight: '600', color: '#333', marginTop: '4px'}}>Hook: "{camp.hook}"</div>}
                                        {camp.location && <div style={{fontSize: '12px', color: '#666', marginTop: '2px'}}>📍 {camp.location}</div>}
                                    </div>
                                    <div style={{marginLeft: '8px'}}>
                                        <button onClick={() => fetchCampaignStats(camp)} className="li-action-btn">
                                            View Stats
                                        </button>
                                    </div>
                                </div>
                                <div style={{padding: '8px 16px', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#fcfcfc', borderTop: '1px solid #f0f0f0'}}>
                                    {deleteConfirmId === camp.code ? (
                                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                            <span style={{fontSize: '12px', color: '#ef4444', fontWeight: '600'}}>Confirm delete?</span>
                                            <button 
                                                onClick={() => handleDeleteCampaign(camp.code)} 
                                                style={{background: '#ef4444', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '4px 8px', color: '#fff', fontSize: '12px'}}
                                            >
                                                Yes
                                            </button>
                                            <button 
                                                onClick={() => setDeleteConfirmId(null)} 
                                                style={{background: '#ddd', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '4px 8px', color: '#333', fontSize: '12px'}}
                                            >
                                                No
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <button 
                                                onClick={() => startEditing(camp)} 
                                                title="Edit Name"
                                                style={{background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button 
                                                onClick={() => handleDeleteCampaign(camp.code)} 
                                                title="Delete Campaign"
                                                style={{background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                        {campaigns.length === 0 && <p style={{color: '#666', textAlign: 'center'}}>No active campaigns.</p>}
                    </div>
                </div>
            </div>
        );
      case 'campaignDetails':
        return (
            <div className="li-card li-create-post-card">
                <div className="li-card-header" style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <button 
                        onClick={() => setActiveView('marketing')} 
                        className="li-btn-outline-secondary"
                        style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 12px', borderRadius: '16px'}}
                    >
                        <ArrowLeft size={16} /> Back
                    </button>
                    {editingId === selectedCampaign?.code ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', flex: 1}}>
                            <input 
                                type="text" 
                                value={tempName} 
                                onChange={(e) => setTempName(e.target.value)}
                                className="li-input"
                                style={{padding: '4px 8px', fontSize: '18px', fontWeight: 'bold'}}
                                autoFocus
                            />
                            <button onClick={() => saveEditing(selectedCampaign)} style={{background:'none', border:'none', color:'#22c55e', cursor:'pointer'}}><Check size={20}/></button>
                            <button onClick={() => setEditingId(null)} style={{background:'none', border:'none', color:'#666', cursor:'pointer'}}><X size={20}/></button>
                        </div>
                    ) : (
                        <h3>Campaign Details: {selectedCampaign?.name}</h3>
                    )}
                </div>
                <div className="li-card-body">
                    <div style={{background: '#f3f2ef', padding: '16px', borderRadius: '8px', marginBottom: '24px', display: 'flex', gap: '24px', alignItems: 'center'}}>
                        <div style={{background: '#fff', padding: '12px', border: '1px solid #ddd', borderRadius: '8px'}}>
                             <QRCode value={selectedCampaign?.url || ''} size={100} />
                        </div>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', flex: 1}}>
                            <div>
                                <span style={{display: 'block', fontSize: '12px', color: '#666', textTransform: 'uppercase'}}>Tracking Code</span>
                                <span style={{fontSize: '18px', fontWeight: '600', fontFamily: 'monospace'}}>{selectedCampaign?.code}</span>
                            </div>
                            <div>
                                <span style={{display: 'block', fontSize: '12px', color: '#666', textTransform: 'uppercase'}}>Total Signups</span>
                                <span style={{fontSize: '18px', fontWeight: '600', color: '#0a66c2'}}>{campaignStats.loading ? '...' : campaignStats.users.length}</span>
                            </div>
                            <div style={{gridColumn: 'span 2'}}>
                                <span style={{display: 'block', fontSize: '12px', color: '#666', textTransform: 'uppercase'}}>Hook / Message</span>
                                <span style={{fontSize: '16px', fontStyle: 'italic'}}>"{selectedCampaign?.hook || 'N/A'}"</span>
                            </div>
                            <div style={{gridColumn: 'span 2'}}>
                                <span style={{display: 'block', fontSize: '12px', color: '#666', textTransform: 'uppercase'}}>Location</span>
                                <span style={{fontSize: '14px', fontWeight: '500'}}>{selectedCampaign?.location || 'N/A'}</span>
                            </div>
                            {selectedCampaign?.notes && selectedCampaign.notes.length > 0 && (
                                <div style={{gridColumn: 'span 2'}}>
                                    <span style={{display: 'block', fontSize: '12px', color: '#666', textTransform: 'uppercase'}}>Strategy Notes</span>
                                    <ul style={{margin: '4px 0 0 0', paddingLeft: '20px', fontSize: '14px', color: '#333'}}>
                                        {selectedCampaign.notes.map((note, i) => (
                                            <li key={i}>{note}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div style={{gridColumn: 'span 2'}}>
                                <span style={{display: 'block', fontSize: '12px', color: '#666', textTransform: 'uppercase'}}>Full URL</span>
                                <span style={{fontSize: '14px', fontFamily: 'monospace', color: '#0a66c2', wordBreak: 'break-all'}}>{selectedCampaign?.url}</span>
                            </div>
                        </div>
                    </div>

                    <h4>Signup List</h4>
                    {campaignStats.loading && <p>Loading data...</p>}
                    {!campaignStats.loading && campaignStats.users.length === 0 && <p style={{color: '#666'}}>No signups yet for this campaign.</p>}
                    
                    <div className="li-feed-items">
                        {campaignStats.users.map(u => (
                            <div key={u.uid} className="li-feed-post" style={{padding: '12px', borderBottom: '1px solid #eee'}}>
                                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                                    <div>
                                        <span className="li-post-title" style={{fontSize: '14px'}}>{u.displayName || 'Unknown'}</span>
                                        <div style={{fontSize: '12px', color: '#666'}}>{u.email}</div>
                                    </div>
                                    <div style={{fontSize: '12px', color: '#666', textAlign: 'right'}}>
                                        {u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}
                                        <br/>
                                        {u.createdAt?.seconds ? new Date(u.createdAt.seconds * 1000).toLocaleTimeString() : ''}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Footer with Edit/Delete Actions */}
                <div style={{padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: '#fcfcfc', borderTop: '1px solid #e0e0e0', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px'}}>
                    {deleteConfirmId === selectedCampaign?.code ? (
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span style={{fontSize: '12px', color: '#ef4444', fontWeight: '600'}}>Confirm delete?</span>
                            <button 
                                onClick={() => {
                                    handleDeleteCampaign(selectedCampaign.code);
                                    setActiveView('marketing'); // Go back after delete
                                }} 
                                style={{background: '#ef4444', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '4px 8px', color: '#fff', fontSize: '12px'}}
                            >
                                Yes
                            </button>
                            <button 
                                onClick={() => setDeleteConfirmId(null)} 
                                style={{background: '#ddd', border: 'none', borderRadius: '4px', cursor: 'pointer', padding: '4px 8px', color: '#333', fontSize: '12px'}}
                            >
                                No
                            </button>
                        </div>
                    ) : (
                        <>
                            <button 
                                onClick={() => setActiveView('printBrief')} 
                                title="Print Brief"
                                style={{background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#0a66c2', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                            >
                                <FileText size={20} />
                            </button>
                            <button 
                                onClick={() => setActiveView('printLabels')} 
                                title="Print Labels"
                                style={{background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#0a66c2', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                            >
                                <Printer size={20} />
                            </button>
                            <button 
                                onClick={() => startEditing(selectedCampaign)} 
                                title="Edit Name"
                                style={{background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                            >
                                <Edit2 size={20} />
                            </button>
                            <button 
                                onClick={() => handleDeleteCampaign(selectedCampaign.code)} 
                                title="Delete Campaign"
                                style={{background: 'none', border: 'none', cursor: 'pointer', padding: '6px', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center'}}
                            >
                                <Trash2 size={20} />
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
      case 'platformStats':
        return (
            <div className="li-card li-create-post-card">
                <div className="li-card-header">
                    <h3>{t('adminDashboard.stats.title')}</h3>
                </div>
                <div className="li-card-body">
                    <div>
                        {/* Total Assets Row */}
                        <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px'}}>
                            <h4 style={{margin: 0, fontSize: '14px', textTransform: 'uppercase', color: '#666'}}>{t('adminDashboard.stats.totalAssets')}</h4>
                            <button onClick={fetchTotalStats} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: '4px'}} title={t('adminDashboard.stats.refreshTotals')}>
                                <RefreshCw size={14} />
                            </button>
                        </div>
                        
                        <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px'}}>
                            <div style={{background: '#f8f9fa', padding: '16px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e9ecef'}}>
                                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#333'}}>{totalStats.users}</div>
                                <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>{t('adminDashboard.stats.totalUsers')}</div>
                            </div>
                            <div style={{background: '#f8f9fa', padding: '16px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e9ecef'}}>
                                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#333'}}>{totalStats.orgs}</div>
                                <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>{t('adminDashboard.stats.organizations')}</div>
                            </div>
                            <div style={{background: '#f8f9fa', padding: '16px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e9ecef'}}>
                                <div style={{fontSize: '24px', fontWeight: 'bold', color: '#333'}}>{totalStats.vtcs}</div>
                                <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>{t('adminDashboard.stats.aiTwins')}</div>
                            </div>
                        </div>

                        {/* Growth Stats Row Header */}
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                            <h4 style={{margin: 0, fontSize: '14px', textTransform: 'uppercase', color: '#666'}}>{t('adminDashboard.stats.growth', { range: t(`adminDashboard.stats.ranges.${timeRange}`) })}</h4>
                            <div style={{display: 'flex', gap: '8px'}}>
                                {['today', 'week', 'month', 'all'].map(r => (
                                    <button 
                                        key={r}
                                        onClick={() => fetchGrowthStats(r)}
                                        className={timeRange === r ? 'li-btn-primary' : 'li-btn-outline-secondary'}
                                        style={{textTransform: 'capitalize', fontSize: '12px', padding: '4px 10px', borderRadius: '14px'}}
                                    >
                                        {t(`adminDashboard.stats.ranges.${r}`)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {statsLoading ? (
                            <p style={{padding: '24px', textAlign: 'center'}}>{t('loading')}</p>
                        ) : (
                            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px'}}>
                                {/* KPI Card: Users */}
                                <div style={{background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', textAlign: 'center'}}>
                                    <div style={{color: '#666', fontSize: '14px', marginBottom: '8px'}}>{t('adminDashboard.stats.newUsers')}</div>
                                    <div style={{fontSize: '36px', fontWeight: 'bold', color: '#0a66c2'}}>{statsData.users}</div>
                                </div>
                                
                                {/* KPI Card: Simulations */}
                                <div style={{background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', textAlign: 'center'}}>
                                    <div style={{color: '#666', fontSize: '14px', marginBottom: '8px'}}>{t('adminDashboard.stats.simulationsStarted')}</div>
                                    <div style={{fontSize: '36px', fontWeight: 'bold', color: '#0a66c2'}}>{statsData.simulations}</div>
                                </div>

                                {/* KPI Card: Campaigns */}
                                <div style={{background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '24px', textAlign: 'center'}}>
                                    <div style={{color: '#666', fontSize: '14px', marginBottom: '8px'}}>{t('adminDashboard.stats.campaignsCreated')}</div>
                                    <div style={{fontSize: '36px', fontWeight: 'bold', color: '#0a66c2'}}>{statsData.campaigns}</div>
                                </div>
                            </div>
                        )}
                    </div>
                    <p style={{marginTop: '24px', fontSize: '12px', color: '#999', textAlign: 'center'}}>
                        {t('adminDashboard.stats.disclaimer')}
                    </p>
                </div>
            </div>
        );
      case 'printLabels':
        return (
            <div className="li-card li-create-post-card" style={{ maxWidth: '100%', padding: 0 }}>
                {/* Print Control Bar (Hidden on Print) */}
                <div className="no-print" style={{ borderBottom: '1px solid #ddd', background: '#fff' }}>
                    {/* Row 1: Nav */}
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                        <button 
                            onClick={() => setActiveView('campaignDetails')} 
                            className="li-btn-outline-secondary"
                            style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 12px', borderRadius: '16px'}}
                        >
                            <ArrowLeft size={16} /> Back to Campaign
                        </button>
                    </div>
                    
                    {/* Row 2: Controls */}
                    <div style={{ padding: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Rows</label>
                            <input type="number" value={labelConfig.rows} onChange={(e) => setLabelConfig({...labelConfig, rows: parseInt(e.target.value)})} style={{width: '50px'}} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Cols</label>
                            <input type="number" value={labelConfig.cols} onChange={(e) => setLabelConfig({...labelConfig, cols: parseInt(e.target.value)})} style={{width: '50px'}} />
                        </div>
                        
                        {/* Dimensions */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Label Width (mm)</label>
                            <input type="number" value={labelConfig.cellWidth} onChange={(e) => setLabelConfig({...labelConfig, cellWidth: parseInt(e.target.value)})} style={{width: '80px'}} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Label Height (mm)</label>
                            <input type="number" value={labelConfig.cellHeight} onChange={(e) => setLabelConfig({...labelConfig, cellHeight: parseInt(e.target.value)})} style={{width: '80px'}} />
                        </div>
                        
                        {/* Margins */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Top Margin (mm)</label>
                            <input type="number" value={labelConfig.marginTop} onChange={(e) => setLabelConfig({...labelConfig, marginTop: parseInt(e.target.value)})} style={{width: '80px'}} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Left Margin (mm)</label>
                            <input type="number" value={labelConfig.marginLeft} onChange={(e) => setLabelConfig({...labelConfig, marginLeft: parseInt(e.target.value)})} style={{width: '80px'}} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Right Margin (mm)</label>
                            <input type="number" value={labelConfig.marginRight} onChange={(e) => setLabelConfig({...labelConfig, marginRight: parseInt(e.target.value)})} style={{width: '80px'}} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Spacing (mm)</label>
                            <input type="number" value={labelConfig.gap} onChange={(e) => setLabelConfig({...labelConfig, gap: parseInt(e.target.value)})} style={{width: '80px'}} />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>QR Size (px)</label>
                            <input type="number" value={labelConfig.qrSize} onChange={(e) => setLabelConfig({...labelConfig, qrSize: parseInt(e.target.value)})} style={{width: '60px'}} />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600' }}>Font</label>
                            <input type="number" value={labelConfig.fontSize} onChange={(e) => setLabelConfig({...labelConfig, fontSize: parseInt(e.target.value)})} style={{width: '50px'}} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '12px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                <input type="checkbox" checked={labelConfig.shape === 'round'} onChange={(e) => setLabelConfig({...labelConfig, shape: e.target.checked ? 'round' : 'rect'})} />
                                Round
                            </label>
                        </div>
                        <button onClick={() => window.print()} className="li-btn-primary" style={{marginLeft: 'auto'}}>
                            Print Labels
                        </button>
                    </div>
                </div>

                {/* Print Canvas (A4 Simulation) */}
                <div style={{ background: '#ccc', padding: '20px', overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <div className="print-area" style={{ 
                        background: 'white', 
                        width: '210mm', 
                        minHeight: '297mm', 
                        paddingTop: `${labelConfig.marginTop}mm`,
                        paddingLeft: `${labelConfig.marginLeft}mm`,
                        paddingRight: `${labelConfig.marginRight}mm`,
                        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                        display: 'grid',
                        gridTemplateColumns: `repeat(${labelConfig.cols}, ${labelConfig.cellWidth}mm)`,
                        gridTemplateRows: `repeat(${labelConfig.rows}, ${labelConfig.cellHeight}mm)`,
                        gap: `${labelConfig.gap}mm`,
                        justifyContent: 'start',
                        alignContent: 'start'
                    }}>
                        {Array.from({ length: labelConfig.rows * labelConfig.cols }).map((_, i) => (
                            <div key={i} style={{ 
                                border: '1px dashed #ddd', 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                padding: `${labelConfig.padding}px`,
                                borderRadius: labelConfig.shape === 'round' ? '50%' : '4px',
                                overflow: 'hidden',
                                width: '100%',
                                height: '100%'
                            }}>
                                <QRCode value={selectedCampaign?.url || ''} size={labelConfig.qrSize} />
                                {labelConfig.showHook && selectedCampaign?.hook && (
                                    <div style={{ marginTop: '4px', textAlign: 'center', fontSize: `${labelConfig.fontSize}px`, fontWeight: '600', lineHeight: '1.2' }}>
                                        {selectedCampaign.hook}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Print Styles Injection */}
                <style>{`
                    @media print {
                        /* Hide everything by default */
                        body, #root {
                            visibility: hidden;
                            margin: 0;
                            padding: 0;
                        }
                        
                        /* Make the print area visible and positioned */
                        .print-area {
                            visibility: visible !important;
                            position: fixed; /* Fixed places it relative to the page */
                            left: 0;
                            top: 0;
                            width: 100%;
                            min-height: 100%;
                            z-index: 99999;
                            background: white;
                            /* Padding is handled inline by state */
                            display: grid !important;
                            overflow: visible;
                            box-shadow: none !important;
                            border: none !important;
                        }
                        
                        .print-area > div {
                            border: none !important; /* Remove dashed lines when printing */
                        }

                        /* Ensure content inside print area is visible */
                        .print-area * {
                            visibility: visible !important;
                        }

                        @page {
                            size: A4;
                            margin: 0;
                        }
                    }
                `}</style>
            </div>
        );
      case 'printBrief':
        return (
            <div className="li-card li-create-post-card" style={{ maxWidth: '100%', padding: 0 }}>
                {/* Control Bar */}
                <div className="no-print" style={{ borderBottom: '1px solid #ddd', background: '#fff' }}>
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button 
                            onClick={() => setActiveView('campaignDetails')} 
                            className="li-btn-outline-secondary"
                            style={{display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', padding: '6px 12px', borderRadius: '16px'}}
                        >
                            <ArrowLeft size={16} /> Back to Campaign
                        </button>
                        <button onClick={() => window.print()} className="li-btn-primary">
                            Print Brief
                        </button>
                    </div>
                </div>

                {/* Brief Canvas (A4) */}
                <div style={{ background: '#ccc', padding: '20px', overflow: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <div className="print-area" style={{ 
                        background: 'white', 
                        width: '210mm', 
                        minHeight: '297mm', 
                        padding: '20mm', 
                        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
                        color: '#000',
                        fontFamily: 'Arial, sans-serif'
                    }}>
                        <div style={{borderBottom: '2px solid #000', paddingBottom: '20px', marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                            <div>
                                <h1 style={{margin: '0 0 5px 0', fontSize: '28px', textTransform: 'uppercase', letterSpacing: '-1px'}}>Distribution Mission</h1>
                                <div style={{fontSize: '16px', fontWeight: '600', color: '#444'}}>SOCIAL IMPACT PROJECT: Connecting local people with job opportunities.</div>
                                <div style={{fontSize: '20px', fontWeight: 'bold', marginTop: '12px'}}>{selectedCampaign?.name}</div>
                            </div>
                            <div style={{textAlign: 'right'}}>
                                <div style={{fontSize: '12px', textTransform: 'uppercase', color: '#666'}}>Tracking Code</div>
                                <div style={{fontSize: '24px', fontFamily: 'monospace', fontWeight: 'bold'}}>{selectedCampaign?.code}</div>
                            </div>
                        </div>

                        <div style={{marginBottom: '30px'}}>
                            <h2 style={{fontSize: '16px', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '5px', marginBottom: '15px'}}>Target Location</h2>
                            <div style={{fontSize: '24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px'}}>
                                <span>📍</span> {selectedCampaign?.location || 'General Distribution'}
                            </div>
                        </div>

                        <div style={{display: 'flex', gap: '40px', marginBottom: '40px', alignItems: 'center', border: '1px solid #000', padding: '20px', borderRadius: '8px'}}>
                            <div style={{background: '#fff'}}>
                                <QRCode value={selectedCampaign?.url || ''} size={120} />
                            </div>
                            <div style={{flex: 1}}>
                                <h2 style={{fontSize: '16px', textTransform: 'uppercase', margin: '0 0 10px 0'}}>Sticker Message</h2>
                                <div style={{fontSize: '24px', fontStyle: 'italic', fontWeight: 'bold', color: '#0a66c2'}}>
                                    "{selectedCampaign?.hook}"
                                </div>
                                <p style={{fontSize: '14px', color: '#666', marginTop: '8px'}}>This is what people will read before they scan.</p>
                            </div>
                        </div>

                        <div style={{marginBottom: '40px'}}>
                            <h2 style={{fontSize: '16px', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '5px', marginBottom: '15px'}}>Strategy & Notes</h2>
                            <ul style={{fontSize: '16px', lineHeight: '1.6'}}>
                                {selectedCampaign?.notes && selectedCampaign.notes.length > 0 ? (
                                    selectedCampaign.notes.map((note, i) => <li key={i}>{note}</li>)
                                ) : (
                                    <li>No specific notes. Distribute in high-traffic areas.</li>
                                )}
                            </ul>
                        </div>

                        <div>
                            <h2 style={{fontSize: '16px', textTransform: 'uppercase', borderBottom: '1px solid #ccc', paddingBottom: '5px', marginBottom: '15px'}}>Distributor Instructions</h2>
                            <ul style={{fontSize: '14px', lineHeight: '1.8', color: '#333'}}>
                                <li><strong>Be Respectful:</strong> Ask permission before sticking on shop windows.</li>
                                <li><strong>Don't Cover Stuff:</strong> Never cover safety signs, maps, or official notices.</li>
                                <li><strong>Proof of Work:</strong> Take a photo of each sticker you place.</li>
                                <li><strong>Best Spots:</strong> Bus stops, queue lines, notice boards, near coffee machines.</li>
                            </ul>
                        </div>
                        
                        <div style={{marginTop: '40px', borderTop: '2px solid #000', paddingTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                            <div>
                                <h3 style={{margin: '0 0 8px 0', fontSize: '14px', textTransform: 'uppercase', color: '#666'}}>Operated By</h3>
                                <div style={{fontSize: '16px', fontWeight: 'bold'}}>gClouds IT Consultancy Ltd</div>
                                <div style={{fontSize: '12px', color: '#333'}}>Hatfield, Hertfordshire, UK</div>
                                <div style={{fontSize: '12px', color: '#333'}}>Registered in England & Wales No. 12220173</div>
                                <div style={{fontSize: '12px', color: '#0a66c2', marginTop: '4px'}}>Google Cloud Partner</div>
                            </div>
                            <div style={{textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                <div style={{fontSize: '10px', marginBottom: '4px', textTransform: 'uppercase'}}>Verify Company</div>
                                <QRCode value="https://www.gclouds.co.uk" size={60} />
                                <div style={{fontSize: '10px', marginTop: '4px'}}>www.gclouds.co.uk</div>
                            </div>
                        </div>
                        
                        <div style={{marginTop: '20px', textAlign: 'center', fontSize: '10px', color: '#ccc'}}>
                            Generated by POD+ Admin • {new Date().toLocaleDateString()}
                        </div>
                    </div>
                </div>

                {/* Reuse the Print Styles from printLabels (updated to handle block display) */}
                <style>{`
                    @media print {
                        body, #root { visibility: hidden; margin: 0; padding: 0; }
                        .print-area {
                            visibility: visible !important;
                            position: fixed;
                            left: 0;
                            top: 0;
                            width: 100%;
                            min-height: 100%;
                            z-index: 99999;
                            background: white;
                            padding: 20mm !important;
                            display: block !important; /* Force block for document flow */
                            overflow: visible;
                            box-shadow: none !important;
                            border: none !important;
                        }
                        .print-area * { visibility: visible !important; }
                        @page { size: A4; margin: 0; }
                    }
                `}</style>
            </div>
        );
      default:
        return null;
    }
  };

  // Fallback avatar logic
  const defaultCover = ""; // No cover image
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";

  return (
    <div className="li-layout">
      <div className="li-container" style={(activeView === 'printLabels' || activeView === 'printBrief') ? { display: 'flex', gap: '24px' } : {}}>
        {/* Left Sidebar Navigation */}
        <aside className="li-sidebar-left" style={(activeView === 'printLabels' || activeView === 'printBrief') ? { width: '225px', flexShrink: 0 } : {}}>
            {/* Profile Card (Mini) */}
            <div className="li-card li-profile-card">
                <div className="li-card-background" style={{ backgroundColor: '#a0b4b7' }}></div>
                <div className="li-card-content">
                    <img 
                        src={userProfile?.avatar_url || defaultAvatar} 
                        alt="Profile" 
                        className="li-avatar"
                    />
                    <div className="li-identity">
                        <h3 className="li-name">
                        {userProfile?.firstName} {userProfile?.lastName || 'Admin'}
                        </h3>
                        <p className="li-headline">Administrator</p>
                    </div>
                </div>
            </div>

            {/* Admin Navigation */}
            <div className="li-card li-shortcuts-card">
                <div style={{padding: '12px 0'}}>
                    
                    {/* Talent Cards Group */}
                    <div style={{padding: '0 16px', marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase'}}>{t('adminDashboard.menu.talentCards')}</div>
                    <ul className="li-list-groups" style={{marginBottom: '16px'}}>
                        <li onClick={() => setActiveView('talentCardSearch')} className={activeView === 'talentCardSearch' ? 'active-nav-item' : ''}>
                            <Bot size={16} /> {t('adminDashboard.talentCardSearchTitle')}
                        </li>
                    </ul>

                    {/* Organizations Group */}
                    <div style={{padding: '0 16px', marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase'}}>{t('adminDashboard.menu.organizations')}</div>
                    <ul className="li-list-groups" style={{marginBottom: '16px'}}>
                        <li onClick={() => setActiveView('createOrg')} className={activeView === 'createOrg' ? 'active-nav-item' : ''}>
                            <PlusCircle size={16} /> {t('adminDashboard.createOrgTitle')}
                        </li>
                        <li onClick={() => setActiveView('orgList')} className={activeView === 'orgList' ? 'active-nav-item' : ''}>
                            <Building size={16} /> {t('adminDashboard.existingOrgsTitle')}
                        </li>
                    </ul>

                    {/* Users Group */}
                    <div style={{padding: '0 16px', marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase'}}>{t('adminDashboard.menu.users')}</div>
                    <ul className="li-list-groups" style={{marginBottom: '16px'}}>
                        <li onClick={() => setActiveView('assignUser')} className={activeView === 'assignUser' ? 'active-nav-item' : ''}>
                            <Users size={16} /> {t('adminDashboard.assignConfigTitle')}
                        </li>
                    </ul>

                    {/* Marketing Group */}
                    <div style={{padding: '0 16px', marginBottom: '4px', fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase'}}>{t('adminDashboard.menu.marketing')}</div>
                    <ul className="li-list-groups">
                        <li onClick={() => { setActiveView('platformStats'); fetchTotalStats(); fetchGrowthStats('today'); }} className={activeView === 'platformStats' ? 'active-nav-item' : ''}>
                            <BarChart2 size={16} /> {t('adminDashboard.stats.title')}
                        </li>
                        <li onClick={() => setActiveView('marketing')} className={activeView === 'marketing' || activeView === 'campaignDetails' ? 'active-nav-item' : ''}>
                            <QrCode size={16} /> Marketing & QR
                        </li>
                    </ul>

                </div>
            </div>
        </aside>

        {/* Main Feed Content */}
        <main className="li-main-feed" style={(activeView === 'printLabels' || activeView === 'printBrief') ? { maxWidth: '100%', flex: 1 } : {}}>
            {renderActiveView()}
        </main>

        {/* Right Sidebar */}
        {(activeView !== 'printLabels' && activeView !== 'printBrief') && (
            <aside className="li-sidebar-right">
                <div className="li-card li-news-card">
                    <div className="li-card-header">
                        <h3>Quick Actions</h3>
                    </div>
                    <div className="li-card-body">
                        <button onClick={() => navigate('/invite-employees')} className="li-btn-outline-primary" style={{marginBottom:'12px'}}>
                            <Mail size={16} style={{marginRight:'8px', verticalAlign:'middle'}}/> 
                            {t('hrDashboard.inviteEmployeesTitle')}
                        </button>
                    </div>
                </div>
                <div className="li-footer-links">
                <Link to="/about">{t('footer.about')}</Link>
                <Link to="/accessibility">{t('footer.accessibility')}</Link>
                <Link to="/help-center">{t('footer.helpCenter')}</Link>
                <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                <span>{t('auth.footer.rights')}</span>
            </div>
            </aside>
        )}
      </div>
      <TalentCardModal 
        isOpen={isModalOpen} 
        onRequestClose={() => setIsModalOpen(false)} 
        card={selectedCard} 
      />
    </div>
  );
}

export default AdminDashboard;